import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Eyebrow } from "@/components/Reveal";

const CARD_META = [
  { card: "/products/venus-signature-card.jpg", id: "venus-signature-cutlery", name: "Signature", line: "Distinct by Design. Made for Special Moments." },
  { card: "/products/venus-aura-card.jpg", id: "venus-aura-cutlery", name: "Aura", line: "Subtle Beauty. Lasting Impressions." },
  { card: "/products/venus-orion-card.jpg", id: "venus-orion-cutlery", name: "Orion", line: "Grace in Form. Strength in Everyday." },
  { card: "/products/venus-zen-card.jpg", id: "venus-zen-cutlery", name: "Zen", line: "Balanced Design. Peaceful Moments. Everyday Luxury." },
];

export default function VenusGifting() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    document.title = "Venus Gift Sets — THE GOOD GRID";
    api.get("/products", { params: { brand: "VENUS" } }).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="venus-gifting-page" className="bg-charcoal text-ivory min-h-screen pt-28 lg:pt-36 pb-24">
      <div className="px-4 sm:px-8 lg:px-16">
        <Reveal><Eyebrow>Venus × THE GOOD GRID</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl mt-4 tracking-tight max-w-4xl leading-[1.05]">
            Timeless elegance,<br /><span className="italic text-champagne-light">for every occasion.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-ivory/50 mt-6 max-w-xl leading-relaxed">
            Gift-boxed Venus cutlery sets in premium 18/10 stainless steel — each presented in a ribboned signature box, ready for executive tables, festive hampers and client gifting. Crafted for meaningful connections.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 mt-16">
          {CARD_META.map((c, i) => (
            <Reveal key={c.id} delay={(i % 2) * 0.12}>
              <Link
                to={`/product/${c.id}`}
                data-testid={`venus-card-${c.id}`}
                className="group relative block overflow-hidden border border-white/10"
              >
                <img
                  src={c.card}
                  alt={`Venus ${c.name} cutlery set in gift box`}
                  loading="lazy"
                  className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)]"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#121212]/85 via-transparent to-transparent p-6 sm:p-8 flex justify-end">
                  <span className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-ivory bg-[#121212]/70 backdrop-blur-sm border border-white/15 px-4 py-2.5 group-hover:border-champagne group-hover:text-champagne transition-colors duration-300">
                    View Set <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 border border-champagne/30 bg-[#151210] p-8 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <Reveal><Eyebrow>Festive & Bulk Programmes</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-5xl mt-4 tracking-tight">Festive bundle pricing, <span className="italic text-champagne-light">on request.</span></h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-ivory/50 mt-5 max-w-2xl text-sm leading-relaxed">
                Diwali, New Year and milestone programmes built around Venus gift sets — mixed finishes, custom sleeves, message cards and multi-city dispatch. Tell us your quantity and budget per gift; we will return a curated bundle proposal.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex flex-wrap gap-2 mt-7">
                {["Silver (Mirror)", "Gold", "Rose Gold"].map((f) => (
                  <span key={f} className="text-[10px] font-mono uppercase tracking-[0.18em] border border-white/15 text-ivory/60 px-3 py-1.5">{f}</span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.25}>
              <Link
                to="/request-quote"
                data-testid="venus-bundle-quote"
                className="group flex items-center justify-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-7 py-5 hover:bg-champagne-light transition-colors duration-300 w-full"
              >
                Request Bundle Quote <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <p className="text-ivory/30 text-xs mt-4 text-center">Response within one business day.</p>
            </Reveal>
          </div>
        </div>

        {products.length > 0 && (
          <p className="text-ivory/30 text-xs mt-10">Venus is a brand of J.K. Khurana Group. Products are sourced and curated by THE GOOD GRID for corporate gifting.</p>
        )}
      </div>
    </div>
  );
}
